"use client";

import { useEffect, useState } from "react";
import {
  api,
  type UnifiedSentimentCategory,
  type UnifiedSentimentResponse,
} from "@/lib/api";

/**
 * Unified Stocktwits-style sentiment score for the market detail page.
 * Single 0-100 number combining trade flow (60%), post sentiment (20%),
 * and news sentiment (20%). Replaces the three discrete gauges per
 * founder spec.
 *
 * Hidden silently when the backend returns null (non-binary markets) or
 * when no signal has data — there's no useful score to show.
 */

const CATEGORY_LABEL: Record<UnifiedSentimentCategory, string> = {
  extremely_bullish: "Extremely Bullish",
  bullish: "Bullish",
  slightly_bullish: "Slightly Bullish",
  neutral: "Neutral",
  slightly_bearish: "Slightly Bearish",
  bearish: "Bearish",
  extremely_bearish: "Extremely Bearish",
};

function colorFor(category: UnifiedSentimentCategory): string {
  switch (category) {
    case "extremely_bullish":
    case "bullish":
      return "var(--color-yes)";
    case "slightly_bullish":
      return "var(--color-yes)";
    case "neutral":
      return "var(--color-text2)";
    case "slightly_bearish":
    case "bearish":
    case "extremely_bearish":
      return "var(--color-no)";
  }
}

export function UnifiedSentimentScore({
  marketSlug,
}: {
  marketSlug: string;
}) {
  const [data, setData] = useState<UnifiedSentimentResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .getMarketUnifiedSentiment(marketSlug)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        // silent — gauge is decorative
      });
    return () => {
      cancelled = true;
    };
  }, [marketSlug]);

  if (!data) return null;
  const { score, category, breakdown } = data;
  const anySignal =
    breakdown.trades.sampleSize > 0 ||
    breakdown.posts.sampleSize > 0 ||
    breakdown.news.sampleSize > 0;
  if (!anySignal) return null;

  const color = colorFor(category);
  const label = CATEGORY_LABEL[category];

  return (
    <section
      className="mb-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3"
      aria-label="Unified market sentiment"
    >
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.6px] text-[var(--color-text3)]">
          Sentiment · last 24h
        </span>
        <span
          className="text-[11px] font-bold tabular-nums"
          style={{ color }}
        >
          {label}
        </span>
      </div>

      {/* Single horizontal bar — score positions a marker along bear→bull. */}
      <div
        className="relative h-2 w-full overflow-hidden rounded-full bg-[var(--color-border)]"
        role="img"
        aria-label={`Score ${score} of 100 — ${label}`}
      >
        <div
          className="absolute inset-y-0 left-0"
          style={{
            width: `${score}%`,
            background:
              "linear-gradient(90deg, var(--color-no) 0%, var(--color-text3) 50%, var(--color-yes) 100%)",
          }}
        />
        <div
          className="absolute top-1/2 h-3 w-[2px] -translate-y-1/2"
          style={{
            left: `calc(${score}% - 1px)`,
            background: color,
          }}
        />
      </div>

      <div className="mt-1.5 flex items-baseline justify-between text-[11px] text-[var(--color-text3)]">
        <span>Bearish</span>
        <span className="font-mono tabular-nums" style={{ color }}>
          {score}/100
        </span>
        <span>Bullish</span>
      </div>

      {/* Breakdown — three small chips so the user can see what's
          driving the score. Hidden when sampleSize is 0. */}
      <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
        <Chip
          label="Trades"
          score={breakdown.trades.score}
          n={breakdown.trades.sampleSize}
          weight={breakdown.trades.weight}
        />
        <Chip
          label="Posts"
          score={breakdown.posts.score}
          n={breakdown.posts.sampleSize}
          weight={breakdown.posts.weight}
        />
        <Chip
          label="News"
          score={breakdown.news.score}
          n={breakdown.news.sampleSize}
          weight={breakdown.news.weight}
        />
      </div>
    </section>
  );
}

function Chip({
  label,
  score,
  n,
  weight,
}: {
  label: string;
  score: number | null;
  n: number;
  weight: number;
}) {
  if (n === 0 || score === null) {
    return (
      <span className="rounded-md border border-[var(--color-border)] px-1.5 py-[1px] text-[var(--color-text3)]">
        {label}: —
      </span>
    );
  }
  const rounded = Math.round(score);
  return (
    <span className="rounded-md border border-[var(--color-border)] px-1.5 py-[1px] text-[var(--color-text2)]">
      {label}: {rounded}/100
      <span className="ml-1 text-[var(--color-text3)]">
        ({n} · {Math.round(weight * 100)}%)
      </span>
    </span>
  );
}
